import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isOwner, lastSeen } from "./Account";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Upload, RefreshCw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { PageHeader, Panel, Empty } from "../components/Widgets";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { MerchantDirectory } from "../components/MerchantDirectory";
import { CarryHistory } from "../components/Carry";
import { CompanySwitch } from "../components/Companies";
import { RangeReport } from "../components/RangeReport";
import { AttritionReport } from "../components/Attrition";
import { SaasFeeReport } from "../components/SaasFees";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState(null);
  const [company, setCompany] = useState("");
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    await api.delete(`/reports/${del.id}`);
    toast.success("Report deleted"); setDel(null); load();
  };
  const reprocess = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/reports/${r.id}/reprocess`);
      const miss = data.unmatched_agents?.length;
      toast.success(`${monthLabel(r.month)} re-processed · ${data.row_count} rows${miss ? ` · ${miss} unmatched agent codes` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader eyebrow={company ? `History · ${company} only` : "History"} title="Reports">
        <CompanySwitch value={company} onChange={setCompany} testId="reports-company-switch" />
        <Button asChild className="bg-blue-500 hover:bg-blue-600 text-white"><Link to="/admin/upload"><Upload size={14} className="mr-2" />Upload</Link></Button>
      </PageHeader>
      <Tabs defaultValue="uploads">
        <TabsList className="mb-4">
          <TabsTrigger value="uploads" data-testid="reports-tab-uploads">Uploaded reports</TabsTrigger>
          <TabsTrigger value="merchants" data-testid="reports-tab-merchants">Merchants A–Z</TabsTrigger>
          <TabsTrigger value="carry" data-testid="reports-tab-carry">Carried balances</TabsTrigger>
          <TabsTrigger value="builder" data-testid="reports-tab-builder">Report builder</TabsTrigger>
          <TabsTrigger value="attrition" data-testid="reports-tab-attrition">Attrition</TabsTrigger>
          <TabsTrigger value="saas" data-testid="reports-tab-saas">SaaS fees</TabsTrigger>
        </TabsList>
        <TabsContent value="saas"><SaasFeeReport company={company} /></TabsContent>
        <TabsContent value="merchants"><MerchantDirectory company={company} /></TabsContent>
        <TabsContent value="carry"><CarryHistory showAgent /></TabsContent>
        <TabsContent value="builder"><RangeReport company={company} /></TabsContent>
        <TabsContent value="attrition"><AttritionReport company={company} /></TabsContent>
        <TabsContent value="uploads">
      <Panel>
        {!reports ? <Empty>Loading…</Empty> : reports.length === 0 ? <Empty>No reports uploaded yet.</Empty> : (
          <div className="overflow-x-auto">
            <table className="tbl w-full" data-testid="reports-table">
              <thead><tr><th>Month</th><th>Company</th><th>File</th><th>Source</th><th className="!text-right">Rows</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>{reports.filter((r) => !company || (r.processor || "Paysafe") === company).map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.id}`}>
                  <td className="font-mono">{monthLabel(r.month)}</td>
                  <td data-testid={`report-company-${r.id}`} className={r.processor === "Fiserv" ? "text-orange-300" : r.processor === "Priority Payments" ? "text-pink-300" : "text-slate-400"}>{r.processor || "Paysafe"}</td>
                  <td className="text-slate-300">{r.filename}</td>
                  <td className="text-slate-400">{r.source || "—"}</td>
                  <td className="text-right font-mono">{r.row_count}</td>
                  <td className="text-right font-mono text-slate-400">{money(r.total_volume)}</td>
                  <td className="text-right font-mono text-emerald-400">{money(r.total_residual)}</td>
                  <td className="text-xs text-slate-500 font-mono">{new Date(r.uploaded_at).toLocaleString()}</td>
                  <td className="text-right whitespace-nowrap">
                    {r.file_path
                      ? <button data-testid={`reprocess-report-${r.id}`} disabled={busy === r.id} onClick={() => reprocess(r)} title="Re-process from saved file"
                          className="mr-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase text-slate-400 hover:text-blue-300 disabled:opacity-50 transition-colors">
                          <RefreshCw size={13} className={busy === r.id ? "animate-spin" : ""} />{busy === r.id ? "Working" : "Re-process"}</button>
                      : <span data-testid={`no-file-${r.id}`} className="mr-3 text-[11px] text-slate-600" title="Uploaded before files were saved">no saved file</span>}
                    {isOwner(user) && <button data-testid={`delete-report-${r.id}`} onClick={() => setDel(r)} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 size={15} /></button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
        </TabsContent>
      </Tabs>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>All {del?.row_count} rows from {del?.filename} will be removed and payouts for {monthLabel(del?.month)} recalculated.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-report" onClick={remove} className="bg-rose-500 hover:bg-rose-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
